import * as THREE from 'three'

const WIREFRAME_META_KEY = '__vybyWireframeModel'
const DEFAULT_MODEL_POSITION_Z = 35
const DEFAULT_MODEL_TARGET_RATIO = 0.32

export const DEFAULT_D3_WIREFRAME_PROPS = {
  'render.mode': 'wireframe',
  'wireframe.style': 'edges',
  'wireframe.color': '#ffffff',
  'wireframe.opacity': 1,
  'wireframe.threshold': 15,
}

const clamp = (value, min, max, fallback) => {
  const next = Number(value)
  if (!Number.isFinite(next)) return fallback
  return Math.max(min, Math.min(max, next))
}

const normalizeRenderMode = (value) => {
  if (value === 'solid' || value === 'wireframe+ghost') return value
  return 'wireframe'
}

const normalizeWireframeStyle = (value) => {
  if (value === 'mesh') return 'mesh'
  return 'edges'
}

const normalizeProps = (props = {}) => {
  const opacity = clamp(props.opacity, 0, 1, 1)
  return {
    renderMode: normalizeRenderMode(props['render.mode']),
    style: normalizeWireframeStyle(props['wireframe.style']),
    color: props['wireframe.color'] || '#ffffff',
    opacity,
    wireOpacity: clamp(props['wireframe.opacity'], 0, 1, 1) * opacity,
    threshold: clamp(props['wireframe.threshold'], 0, 180, 15),
  }
}

const materialList = (material) => {
  if (!material) return []
  return Array.isArray(material) ? material : [material]
}

const cloneRenderableMaterials = (root) => {
  root.traverse((obj) => {
    if (!obj || !obj.isMesh || !obj.material) return
    obj.material = Array.isArray(obj.material)
      ? obj.material.map((mat) => mat && mat.clone ? mat.clone() : mat)
      : obj.material.clone()
  })
}

const rememberMaterialDefaults = (material) => {
  if (!material || material.userData?.__vybyWireframeDefaults) return
  material.userData = {
    ...(material.userData || {}),
    __vybyWireframeDefaults: {
      opacity: typeof material.opacity === 'number' ? material.opacity : 1,
      transparent: !!material.transparent,
      depthWrite: material.depthWrite !== false,
    },
  }
}

const setSolidMaterialMode = (solidRoot, mode, opacity) => {
  const showSolid = mode === 'solid' || mode === 'wireframe+ghost'
  solidRoot.visible = showSolid
  if (!showSolid) return

  const ghost = mode === 'wireframe+ghost'
  solidRoot.traverse((obj) => {
    if (!obj || !obj.isMesh) return
    materialList(obj.material).forEach((material) => {
      rememberMaterialDefaults(material)
      const defaults = material.userData.__vybyWireframeDefaults
      const nextOpacity = ghost
        ? Math.min(0.18, opacity * 0.22)
        : defaults.opacity * opacity

      material.opacity = nextOpacity
      material.transparent = defaults.transparent || nextOpacity < 1
      material.depthWrite = ghost ? false : defaults.depthWrite
      material.needsUpdate = true
    })
  })
}

const copyObjectTransform = (source, target) => {
  target.name = source.name || ''
  target.position.copy(source.position)
  target.quaternion.copy(source.quaternion)
  target.scale.copy(source.scale)
  target.matrix.copy(source.matrix)
  target.matrixAutoUpdate = source.matrixAutoUpdate
  target.visible = source.visible
}

const makeWireGeometry = (geometry, style, threshold) => {
  if (style === 'mesh') return new THREE.WireframeGeometry(geometry)
  return new THREE.EdgesGeometry(geometry, threshold)
}

const cloneWireNode = (source, options, materials, geometries, stats) => {
  const node = new THREE.Group()
  copyObjectTransform(source, node)

  if (source.isMesh && source.geometry) {
    const geometry = makeWireGeometry(source.geometry, options.style, options.threshold)
    const material = new THREE.LineBasicMaterial({
      color: options.color,
      transparent: options.wireOpacity < 1,
      opacity: options.wireOpacity,
      depthTest: true,
      depthWrite: false,
      toneMapped: false,
    })
    const lines = new THREE.LineSegments(geometry, material)
    lines.name = source.name ? `${source.name} Wireframe` : 'Wireframe'
    lines.userData = {
      ...(lines.userData || {}),
      __vybyWireframeLine: true,
    }
    node.add(lines)
    materials.push(material)
    geometries.push(geometry)
    stats.meshCount += 1
  }

  source.children.forEach((child) => {
    node.add(cloneWireNode(child, options, materials, geometries, stats))
  })

  return node
}

const disposeList = (items) => {
  items.forEach((item) => {
    try { item?.dispose?.() } catch (e) { /* noop */ }
  })
}

const boxFromObject = (source) => {
  if (!source) return null
  const box = new THREE.Box3().setFromObject(source)
  if (
    !Number.isFinite(box.min.x) ||
    !Number.isFinite(box.min.y) ||
    !Number.isFinite(box.min.z) ||
    !Number.isFinite(box.max.x) ||
    !Number.isFinite(box.max.y) ||
    !Number.isFinite(box.max.z)
  ) return null
  return box
}

const clampScale = (value) => {
  const next = Number(value)
  if (!Number.isFinite(next) || next <= 0) return 1
  return Math.max(0.001, Math.min(1000, next))
}

const centerObjectOnOrigin = (source) => {
  const box = boxFromObject(source)
  if (!box) return null
  const center = box.getCenter(new THREE.Vector3())
  source.position.sub(center)
  return {
    center,
    size: box.getSize(new THREE.Vector3()),
  }
}

export const getModelPlacementDefaults = (source, zeroFrame = null) => {
  const box = boxFromObject(source)
  if (!box) {
    return {
      'position.x': 0,
      'position.y': 0,
      'position.z': DEFAULT_MODEL_POSITION_Z,
      'scale.x': 1,
      'scale.y': 1,
      'scale.z': 1,
    }
  }

  const size = box.getSize(new THREE.Vector3())
  const maxDim = Math.max(size.x, size.y, size.z)
  const frameMin = Math.min(
    Number(zeroFrame?.width) || 0,
    Number(zeroFrame?.height) || 0
  )
  const targetSize = frameMin > 0 ? frameMin * DEFAULT_MODEL_TARGET_RATIO : 24
  const scale = clampScale(maxDim > 0 ? targetSize / maxDim : 1)

  return {
    'position.x': 0,
    'position.y': 0,
    'position.z': DEFAULT_MODEL_POSITION_Z,
    'scale.x': scale,
    'scale.y': scale,
    'scale.z': scale,
  }
}

const rebuildWireRoot = (root, options) => {
  const meta = root?.userData?.[WIREFRAME_META_KEY]
  if (!meta) return
  if (meta.wireRoot) root.remove(meta.wireRoot)
  disposeList(meta.materials)
  disposeList(meta.geometries)

  const materials = []
  const geometries = []
  const stats = { meshCount: 0 }
  const wireRoot = cloneWireNode(meta.solidRoot, options, materials, geometries, stats)
  wireRoot.name = 'GLB Wireframe'

  meta.wireRoot = wireRoot
  meta.materials = materials
  meta.geometries = geometries
  meta.threshold = options.threshold
  meta.style = options.style
  meta.stats = stats
  root.add(wireRoot)
}

export const createWireframeModel = (source, props = {}) => {
  const root = new THREE.Group()
  root.name = source?.name ? `${source.name} Wireframe Model` : 'Wireframe Model'

  const solidRoot = source.clone(true)
  solidRoot.name = 'GLB Solid Source'
  cloneRenderableMaterials(solidRoot)
  const bounds = centerObjectOnOrigin(solidRoot)

  root.userData = {
    ...(root.userData || {}),
    [WIREFRAME_META_KEY]: {
      solidRoot,
      wireRoot: null,
      materials: [],
      geometries: [],
      threshold: null,
      style: null,
      stats: { meshCount: 0 },
      bounds,
    },
  }
  root.add(solidRoot)
  updateWireframeModel(root, props)
  return root
}

export const updateWireframeModel = (root, props = {}) => {
  const meta = root?.userData?.[WIREFRAME_META_KEY]
  if (!meta) return
  const options = normalizeProps(props)

  if (
    !meta.wireRoot ||
    meta.threshold !== options.threshold ||
    meta.style !== options.style
  ) {
    rebuildWireRoot(root, options)
  }

  setSolidMaterialMode(meta.solidRoot, options.renderMode, options.opacity)
  if (meta.wireRoot) meta.wireRoot.visible = options.renderMode !== 'solid'

  meta.materials.forEach((material) => {
    try {
      material.color = new THREE.Color(options.color)
    } catch (e) {
      material.color = new THREE.Color('#ffffff')
    }
    material.opacity = options.wireOpacity
    material.transparent = options.wireOpacity < 1
    material.needsUpdate = true
  })
}

export const disposeWireframeModel = (root) => {
  const meta = root?.userData?.[WIREFRAME_META_KEY]
  if (!meta) return
  disposeList(meta.materials)
  disposeList(meta.geometries)
  meta.materials = []
  meta.geometries = []
}

export const getWireframeModelStats = (root) => {
  const meta = root?.userData?.[WIREFRAME_META_KEY]
  return meta?.stats || null
}
